import React, { useState, useEffect } from "react";
import axios from "axios";
import { Tabs, Tab, Box } from "@mui/material";
import { DataGridPremium, GridToolbar } from "@mui/x-data-grid-premium";

const DetailedTeamAgent = ({ startDate, endDate, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  // const [pageSize, setPageSize] = useState(5);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleRowExpansion = (id) => {
    const newExpandedRows = new Set(expandedRows);
    if (expandedRows.has(id)) {
      newExpandedRows.delete(id);
    } else {
      newExpandedRows.add(id);
    }
    setExpandedRows(newExpandedRows);
  };

  const getVisibleRows = (rows) => {
    if (!rows) return [];
    return rows.filter(
      (row) => row.parent_id === null || expandedRows.has(row.parent_id),
    );
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/reports/CallCentreSalesReports/DetailedTeamAgentSBIReport?startDate=${startDate}&endDate=${endDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // Transform the data
        const transformedData = [];
        let currentId = 1;

        response.data.data.forEach((item) => {
          // Add main row
          const mainRow = {
            ...item,
            id: currentId,
            parent_id: null,
          };

          // Remove agent_campaign_group_detail from main row
          delete mainRow.agent_campaign_group_detail;
          transformedData.push(mainRow);

          // Add child rows from agent_campaign_group_detail
          if (item.agent_campaign_group_detail?.length > 0) {
            item.agent_campaign_group_detail.forEach((detail) => {
              transformedData.push({
                ...detail,
                id: ++currentId,
                parent_id: mainRow.id,
              });
            });
          }

          currentId++;
        });

        setData({ ...response.data, data: transformedData });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, token]);

  const salesColumns = [
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "campaign_group", headerName: "Campaign", flex: 1 },
    { field: "target_sales", headerName: "Target Sales", flex: 1 },
    { field: "total_sales", headerName: "Total Sales", flex: 1 },
    { field: "sales", headerName: "Sales", flex: 1 },
    { field: "incomplete", headerName: "Incomplete", flex: 1 },
    { field: "pended", headerName: "Pended", flex: 1 },
    { field: "staged", headerName: "Staged", flex: 1 },
    { field: "cancelled", headerName: "Cancelled", flex: 1 },
    {
      field: "total_sales_percentage",
      headerName: "Total Sales % Target",
      flex: 1,
      valueGetter: (params) => {
        const totalSales = params.row.total_sales || 0;
        const targetSales = params.row.target_sales;
        if (targetSales === 0 || !targetSales) return "";
        return ((totalSales / targetSales) * 100).toFixed(0) + "%";
      },
    },
  ];

  const biColumns = [
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "campaign_group", headerName: "Campaign", flex: 1 },
    {
      field: "total_bi",
      headerName: "Total BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.total_bi).toFixed(2) || 0,
    },
    {
      field: "sales_bi",
      headerName: "Sales BI",
      flex: 1,
      valueGetter: (params) => parseFloat(params.row.sales_bi).toFixed(2) || 0,
    },
    {
      field: "target_total_bi",
      headerName: "Target BI",
      flex: 1,
      valueGetter: (params) =>
        parseFloat(params.row.target_total_bi).toFixed(2) || 0,
    },
  ];

  const hoursColumns = [
    { field: "agent_name", headerName: "Agent", flex: 1 },
    { field: "manager_name", headerName: "Manager", flex: 1 },
    { field: "campaign_group", headerName: "Campaign", flex: 1 },
    { field: "target_hours", headerName: "Target Hours", flex: 1 },
    {
      field: "campaign_hours",
      headerName: "Campaign Hours",
      flex: 1,
      valueGetter: (params) =>
        parseFloat(params.row.campaign_hours).toFixed(2) || 0,
    },
    {
      field: "target_sales_per_hour",
      headerName: "Target Sales per Hour",
      flex: 1,
    },
  ];

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!data || data.data.length === 0) return <h3>No Data Available</h3>;

  return (
    <div>
      <Tabs value={tabValue} onChange={handleChange} aria-label="tabs">
        <Tab label="Sales" />
        <Tab label="Hours" />
        <Tab label="BI" />
      </Tabs>

      <Box sx={{ marginTop: 2 }}>
        {tabValue === 0 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...salesColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
          />
        )}
        {tabValue === 1 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...hoursColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
          />
        )}
        {tabValue === 2 && (
          <DataGridPremium
            autoHeight
            rows={getVisibleRows(data.data)}
            columns={[
              {
                field: "__expand",
                headerName: "",
                width: 50,
                renderCell: (params) => {
                  const hasChildren = data.data.some(
                    (row) => row.parent_id === params.row.id,
                  );
                  if (!hasChildren) return null;
                  return (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowExpansion(params.row.id);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "1.2rem",
                        color: "var(--mui-palette-secondary-main)",
                      }}
                    >
                      {expandedRows.has(params.row.id) ? "−" : "+"}
                    </button>
                  );
                },
              },
              ...biColumns,
            ]}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            // pagination
            // pageSize={pageSize}
            // onPageSizeChange={(newSize) => setPageSize(newSize)}
            // initialState={{
            //   pagination: { paginationModel: { pageSize: 10 } },
            // }}
            // pageSizeOptions={[5, 10, 15, 20]}
          />
        )}
      </Box>
    </div>
  );
};

export default DetailedTeamAgent;
